import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

function PurchaseShards() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const shardPackages = [
    { amount: 500, price: 5, value: '500' },
    { amount: 1500, price: 10, value: '1500' },
    { amount: 4000, price: 20, value: '4000' }
  ];

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handlePurchase = async () => {
    if (!selectedAmount) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ shardAmount: selectedAmount.amount })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout page
        window.location.href = data.url;
      } else {
        const errorMsg = data.error || 'Failed to create checkout session';
        const detailsMsg = data.details ? `: ${data.details}` : '';
        console.error('Checkout session error:', errorMsg, detailsMsg);
        setError(errorMsg + detailsMsg);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <div className="text-white text-center py-20">Loading...</div>
      </main>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .shards-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .shards-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>

      <div className="shards-align w-full mb-6">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Purchase Shards</h2>
      </div>

      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Packages */}
          <div className="lg:col-span-2">
            {/* Current Balance */}
            <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="/images/shard_white.png" alt="Shard" className="h-5 w-auto opacity-90" />
                  <span className="text-white/60 text-sm">Current Balance:</span>
                  <span className="text-white font-semibold text-lg">{(user?.shards || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Package Selection */}
            <div>
              <h3 className="text-white font-medium text-base mb-4">Select Package</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {shardPackages.map((pkg) => (
                  <button
                    key={pkg.value}
                    onClick={() => setSelectedAmount(pkg)}
                    className={`relative p-4 rounded-xl border transition-all duration-200 ${
                      selectedAmount?.value === pkg.value
                        ? 'border-accent-blue bg-accent-blue/10'
                        : 'border-dark-border bg-dark-bg/50 hover:border-accent-blue/30'
                    }`}
                  >
                    {selectedAmount?.value === pkg.value && (
                      <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-accent-blue flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <img src="/images/shard_white.png" alt="Shard" className="h-5 w-auto opacity-90" />
                        <span className="text-xl font-semibold text-white">{pkg.amount.toLocaleString()}</span>
                      </div>
                      <div className="text-white/60 text-sm">${pkg.price}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-5 sticky top-5">
              <h3 className="text-white font-medium text-base mb-4">Order Summary</h3>
              
              {selectedAmount ? (
                <>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/60">Shards</span>
                      <span className="text-white font-medium">{selectedAmount.amount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/60">Price</span>
                      <span className="text-white font-semibold">${selectedAmount.price}</span>
                    </div>
                    <div className="pt-3 border-t border-dark-border">
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-sm">Total</span>
                        <span className="text-white font-bold text-lg">${selectedAmount.price}</span>
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-4 bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                      <p className="text-red-400 text-xs">{error}</p>
                    </div>
                  )}

                  {/* Purchase Button */}
                  <button
                    onClick={handlePurchase}
                    disabled={loading}
                    className="w-full py-3 px-4 rounded-xl font-medium text-sm bg-accent-blue text-white hover:bg-accent-blue/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      'Proceed to Checkout'
                    )}
                  </button>

                  {/* Security Note */}
                  <div className="mt-4 pt-4 border-t border-dark-border">
                    <div className="flex items-center gap-1.5 text-white/40 text-xs">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span>Secure payment by Stripe</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-white/40 text-sm">Select a package to continue</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default PurchaseShards;
